import { DestroyRef, inject, Injectable, InjectionToken } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AuthUserInfoValue } from '@core-features/core';
import { BehaviorSubject, filter, Observable } from 'rxjs';

interface HostAdapter {
  isAuthorized$: Observable<boolean>;
  userInfo$: Observable<AuthUserInfoValue>;
  getAccessToken: () => Promise<string>;
  isAccessTokenValid: () => boolean;
  login: () => void;
  logout: () => void;
  logoutLocally: () => void;
  navigateToProfile: () => void;
}

@Injectable({
  providedIn: 'root',
})
export class AuthBridge {
  private _destroyRef = inject(DestroyRef);
  private _isHostRegistered = false;

  private _isValid?: () => boolean;
  private _getToken: () => Promise<string>;
  private _login?: () => void;
  private _logout?: () => void;
  private _navigateToProfile?: () => void;
  private _logoutLocally?: () => void;

  private readonly _authorized$ = new BehaviorSubject<boolean | undefined>(
    undefined,
  );
  readonly isAuthenticated$ = this._authorized$
    .asObservable()
    .pipe(filter((value) => value !== undefined));

  private readonly _userInfo$ = new BehaviorSubject<AuthUserInfoValue>(
    undefined,
  );
  readonly userInfo$ = this._userInfo$.asObservable();

  constructor() {
    this._destroyRef.onDestroy(() => {
      // Complete all BehaviorSubjects to prevent memory leaks
      this._authorized$.complete();
      this._userInfo$.complete();
    });
  }

  getAccessToken(): Promise<string> {
    if (!this._getToken)
      throw new Error('[AuthBridge]: not initialized: getAccessToken');
    return this._getToken();
  }

  isAccessTokenValid(): boolean {
    if (!this._isValid)
      throw new Error('[AuthBridge]: not initialized: isAccessTokenValid');
    return this._isValid();
  }

  login(): void {
    if (!this._login) throw new Error('[AuthBridge]: not initialized: login');
    this._login();
  }

  logout(): void {
    if (!this._logout) throw new Error('[AuthBridge]: not initialized: logout');
    this._logout();
  }

  navigateToProfile(): void {
    if (!this._navigateToProfile)
      throw new Error('[AuthBridge]: not initialized: navigateToProfile');
    this._navigateToProfile();
  }

  logoutLocally(): void {
    if (!this._logoutLocally)
      throw new Error('[AuthBridge]: not initialized: logoutLocally');
    this._logoutLocally();
  }

  registerHost(adapter: HostAdapter): void {
    if (this._isHostRegistered) {
      throw new Error(
        '[AuthBridge]: registerHost() can only be called once at startup',
      );
    }

    this._isHostRegistered = true;

    this._isValid = adapter.isAccessTokenValid;
    this._login = adapter.login;
    this._logout = adapter.logout;
    this._navigateToProfile = adapter.navigateToProfile;
    this._getToken = adapter.getAccessToken;
    this._logoutLocally = adapter.logoutLocally;

    adapter.isAuthorized$
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe(this._authorized$);

    adapter.userInfo$
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe(this._userInfo$);
  }
}

export const AUTH_BRIDGE = new InjectionToken<AuthBridge>('AUTH_BRIDGE', {
  providedIn: 'root',
  factory: () => new AuthBridge(),
});
